import { useAutoTranslation, useUserStore } from "@/entities"
import {
  authControllerSignUp,
  authControllerUpgradeGuest,
  type SignUpDtoReq,
  SignUpDtoReqRegisteredDevice,
} from "@/shared/api/generated"
import { DEFAULT_VOICE_DATA_FOR_REGISTER, LANG_KEYS } from "@/shared/constants"
import { notifyMeasurer } from "@/shared/utils"
import { useMutation } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"

export function useRegisterForm(onGuestUpgradeStarted?: () => void) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<{
    email: string
    password: string
    confirmPassword: string
  }>({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState("")
  const [passConfErr, setPassErr] = useState<string>("")

  const userStore = useUserStore()
  const { t } = useAutoTranslation()
  const formMutation = useMutation({
    mutationFn: async (data: SignUpDtoReq) => {
      if (useUserStore.getState().isGuest) {
        return {
          kind: "guest-upgrade" as const,
          data: await authControllerUpgradeGuest({
            email: data.email,
            password: data.password,
          }),
        }
      }
      return {
        kind: "sign-up" as const,
        data: await authControllerSignUp(data),
      }
    },
    onSuccess: async result => {
      setFormError("")
      if (result.kind === "guest-upgrade") {
        reset()
        setIsLoading(false)
        onGuestUpgradeStarted?.()
        return
      }
      await userStore.login(result.data.access_token)
      reset()
      setIsLoading(false)
      notifyMeasurer()
      //TODO change log post logic (not implemented yet)
      //   const changelogPost = await changelogControllerGetMyPost();
      //   if (changelogPost.content) {
      //     useChangelogStore.getState().setChangeLogPost(changelogPost);
      //   }
      //   reset();
    },
    onError: e => {
      setIsLoading(false)
      if (e instanceof AxiosError) {
        setFormError(
          e.response?.data.message ?? t(LANG_KEYS.SOMETHING_WENT_WRONG)
        )
      }
    },
  })

  const passConf = watch("confirmPassword")

  useEffect(() => {
    if (passConf && passConfErr) {
      setPassErr("")
    }
  }, [passConf])

  return {
    errors,
    control,
    passConfErr,
    isLoading,
    formError,
    setFormError,
    handleSubmit: handleSubmit(values => {
      if (values.password !== values.confirmPassword) {
        setPassErr(t(LANG_KEYS.PASSWORDS_DONT_MATCH))
        return
      }
      setIsLoading(true)
      setFormError("")

      const payload: SignUpDtoReq = {
        email: values.email,
        password: values.password,
        registeredDevice: SignUpDtoReqRegisteredDevice.WEBSITE,
        voiceId: DEFAULT_VOICE_DATA_FOR_REGISTER.VOICE_ID,
        voiceLang: DEFAULT_VOICE_DATA_FOR_REGISTER.VOICE_LANG,
      }

      formMutation.mutate(payload)
    }),
  }
}
